import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Share2, Twitter, Linkedin, Link as LinkIcon, User } from 'lucide-react';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  author: {
    name: string;
    avatar: string;
    role: string;
    bio: string;
  };
  date: string;
  readTime: string;
  category: string;
  tags: string[];
}

// Blog posts data with SEO-friendly slugs
export const blogPostsData: Record<string, BlogPost> = {
  'ochtopus-ai-agent-2-release': {
    slug: 'ochtopus-ai-agent-2-release',
    title: 'Introducing AI Agent 2.0: Your New Coding Partner',
    excerpt: 'We\'re excited to announce the next generation of our AI Agent, featuring improved context understanding, multi-file refactoring, and autonomous task execution.',
    content: `
## The Next Generation of AI-Assisted Development

We're thrilled to announce AI Agent 2.0, the most significant update to our AI coding assistant since launch. This release represents months of research and development, incorporating feedback from thousands of developers who use Ochtopus daily.

### What's New in AI Agent 2.0

**Improved Context Understanding**

The new AI Agent can now understand your entire codebase, not just the file you're working on. It analyzes imports, dependencies, and code patterns across your project to provide more accurate and contextually relevant suggestions.

\`\`\`typescript
// AI Agent now understands your project structure
// and can suggest code that matches your patterns
const userService = new UserService({
  repository: userRepository,
  cache: redisCache,
  logger: appLogger
});
\`\`\`

**Multi-File Refactoring**

Need to rename a function used across 50 files? AI Agent 2.0 can handle it. Simply describe what you want to change, and the agent will:

1. Identify all affected files
2. Preview the changes
3. Apply them atomically with rollback support

**Autonomous Task Execution**

The new agent can execute complex, multi-step tasks autonomously:

- "Add authentication to all API routes"
- "Convert this JavaScript project to TypeScript"
- "Add unit tests for the user service"

### Performance Improvements

We've also made significant performance improvements:

- **50% faster** response times
- **30% reduction** in memory usage
- Support for codebases with **1M+ lines of code**

### Getting Started

AI Agent 2.0 is available today for all Pro and Enterprise users. Simply update to the latest version of Ochtopus to start using it.

\`\`\`bash
# Update Ochtopus to get AI Agent 2.0
brew upgrade ochtopus
\`\`\`

We can't wait to see what you build with AI Agent 2.0!
    `,
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop',
    author: {
      name: 'Sarah Chen',
      avatar: 'SC',
      role: 'Head of Product',
      bio: 'Sarah leads product development at Ochtopus, focusing on AI-powered developer tools.'
    },
    date: 'Dec 20, 2025',
    readTime: '5 min read',
    category: 'Product',
    tags: ['AI', 'Product Update', 'Agent']
  },
  'ochtopus-built-in-testing': {
    slug: 'ochtopus-built-in-testing',
    title: 'How AI is Transforming the Developer Experience',
    excerpt: 'Explore how artificial intelligence is revolutionizing the way developers write, debug, and maintain code. From autocomplete to autonomous agents.',
    content: `
## The AI Revolution in Software Development

The way we write software is changing faster than ever. Artificial intelligence is no longer a futuristic concept—it's here, and it's transforming every aspect of the developer experience.

### From Autocomplete to Autonomous Agents

**The Evolution of Code Assistance**

Remember when autocomplete meant suggesting variable names from your current file? Those days feel ancient now. Modern AI-powered tools can:

- Understand natural language descriptions
- Generate entire functions from comments
- Refactor code while preserving behavior
- Write and run tests automatically

**The Data Behind the Transformation**

Our research shows remarkable productivity gains:

| Metric | Before AI | With AI | Improvement |
|--------|-----------|---------|-------------|
| Lines of code/hour | 50 | 150 | 3x |
| Bug fix time | 45 min | 15 min | 3x faster |
| Code review time | 30 min | 10 min | 3x faster |

### The Human-AI Partnership

The best results come from treating AI as a partner, not a replacement. Here's how top developers work with AI:

1. **Start with intent**: Describe what you want to achieve
2. **Review suggestions**: AI isn't perfect—always review
3. **Iterate together**: Refine the output through conversation
4. **Learn from AI**: Use AI suggestions to learn new patterns

### What's Next?

We're just at the beginning of this transformation. In the coming years, expect:

- AI that understands your entire organization's codebase
- Predictive bug detection before code is even written
- Automated code optimization for performance
- Natural language interfaces for complex operations

The future of development is collaborative—humans and AI working together to build better software, faster.
    `,
    image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=1200&h=600&fit=crop',
    author: {
      name: 'Marcus Johnson',
      avatar: 'MJ',
      role: 'Senior Engineer',
      bio: 'Marcus is a senior engineer at Ochtopus, specializing in AI/ML systems.'
    },
    date: 'Dec 15, 2025',
    readTime: '8 min read',
    category: 'Engineering',
    tags: ['AI', 'Developer Experience', 'Future']
  },
  'ochtopus-keyboard-shortcuts': {
    slug: 'ochtopus-keyboard-shortcuts',
    title: '10 Keyboard Shortcuts That Will 10x Your Productivity',
    excerpt: 'Master these essential keyboard shortcuts in Ochtopus and watch your coding speed skyrocket. From navigation to AI commands, we cover it all.',
    content: `
## Master Your Keyboard, Master Your Code

The fastest developers have one thing in common: they rarely touch their mouse. Here are the 10 keyboard shortcuts that will transform your workflow in Ochtopus.

### 1. Quick File Open (Cmd/Ctrl + P)

Jump to any file in your project instantly. Start typing the filename and press Enter.

**Pro tip**: Use \`:\` followed by a line number to jump directly to that line.

### 2. Command Palette (Cmd/Ctrl + Shift + P)

Access any command in Ochtopus. Can't remember a shortcut? The command palette has you covered.

### 3. AI Chat (Cmd/Ctrl + K)

Open inline AI chat to ask questions about your code or generate new code.

\`\`\`
Cmd/Ctrl + K → "Add error handling to this function"
\`\`\`

### 4. AI Agent (Cmd/Ctrl + Shift + A)

Launch the AI Agent for complex, multi-file tasks.

### 5. Multi-Cursor (Alt + Click)

Add multiple cursors to edit several locations simultaneously. Game-changer for repetitive edits.

### 6. Go to Definition (F12)

Jump to where a function, class, or variable is defined. Works across files.

### 7. Find All References (Shift + F12)

See everywhere a symbol is used in your codebase.

### 8. Toggle Terminal (Cmd/Ctrl + \`)

Quickly show or hide the integrated terminal.

### 9. Split Editor (Cmd/Ctrl + \\)

Work on two files side by side.

### 10. Quick Fix (Cmd/Ctrl + .)

Apply suggested fixes for errors and warnings.

### Bonus: Create Your Own Shortcuts

Go to Settings → Keyboard Shortcuts to customize any shortcut to your preference.

---

Start with these 10, and you'll be flying through code in no time!
    `,
    image: 'https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=1200&h=600&fit=crop',
    author: {
      name: 'Emily Zhang',
      avatar: 'EZ',
      role: 'Developer Advocate',
      bio: 'Emily helps developers get the most out of Ochtopus through tutorials and community engagement.'
    },
    date: 'Dec 10, 2025',
    readTime: '4 min read',
    category: 'Tips & Tricks',
    tags: ['Productivity', 'Shortcuts', 'Tips']
  },
  'ochtopus-enterprise-security': {
    slug: 'ochtopus-enterprise-security',
    title: 'Building Secure Applications with Ochtopus Enterprise',
    excerpt: 'Learn how enterprise teams are using Ochtopus to build secure, compliant applications while maintaining developer productivity.',
    content: `
## Security Without Sacrificing Speed

Enterprise teams face a constant challenge: how do you maintain strict security standards without slowing down development? Ochtopus Enterprise is designed to solve exactly this problem.

### Security Features Built for Enterprise

**Zero Data Retention**

With Zero Data Retention mode, your code never leaves your infrastructure. AI processing happens locally, ensuring complete data privacy.

**SOC 2 Type II Certified**

Ochtopus has achieved SOC 2 Type II certification, demonstrating our commitment to security, availability, and confidentiality.

**SAML/OIDC Single Sign-On**

Integrate with your existing identity provider for seamless, secure authentication:

- Okta
- Azure AD
- Google Workspace
- Custom SAML providers

### Compliance Made Easy

**Audit Logging**

Every action in Ochtopus is logged for compliance purposes:

\`\`\`json
{
  "timestamp": "2025-12-10T10:30:00Z",
  "user": "developer@company.com",
  "action": "ai_agent_invoked",
  "context": "refactoring",
  "files_affected": ["src/auth/login.ts"]
}
\`\`\`

**Role-Based Access Control**

Define granular permissions for different team members:

- **Admin**: Full access to all settings
- **Developer**: Standard development features
- **Viewer**: Read-only access for auditors

### Customer Success Stories

> "Ochtopus Enterprise allowed us to give our developers AI tools while maintaining our strict security requirements." — CISO, Fortune 500 Financial Services

### Getting Started with Enterprise

Contact our sales team to learn how Ochtopus Enterprise can help your organization build secure applications faster.
    `,
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200&h=600&fit=crop',
    author: {
      name: 'David Kim',
      avatar: 'DK',
      role: 'Enterprise Solutions',
      bio: 'David works with enterprise customers to implement secure AI-powered development workflows.'
    },
    date: 'Dec 5, 2025',
    readTime: '6 min read',
    category: 'Enterprise',
    tags: ['Security', 'Enterprise', 'Compliance']
  },
  'ochtopus-vs-vscode-migration': {
    slug: 'ochtopus-vs-vscode-migration',
    title: 'From VS Code to Ochtopus: A Migration Guide',
    excerpt: 'Everything you need to know about migrating from VS Code to Ochtopus. Import your settings, extensions, and keybindings with one click.',
    content: `
## Make the Switch in Minutes

If you're coming from VS Code, you'll feel right at home in Ochtopus. We've designed the migration process to be as seamless as possible.

### One-Click Import

The easiest way to migrate is using our one-click import feature:

1. Open Ochtopus
2. Go to **Settings → Import from VS Code**
3. Click **Import All**

That's it! Your settings, extensions, keybindings, and snippets will be imported automatically.

### What Gets Imported

**Settings**

All your user settings are imported and mapped to their Ochtopus equivalents:

\`\`\`json
{
  "editor.fontSize": 14,
  "editor.tabSize": 2,
  "editor.wordWrap": "on",
  "workbench.colorTheme": "One Dark Pro"
}
\`\`\`

**Extensions**

Ochtopus supports most VS Code extensions. Popular extensions like ESLint, Prettier, and GitLens work out of the box.

**Keybindings**

Your custom keybindings are imported automatically. If you use Vim keybindings, enable Vim mode in Settings → Keyboard.

**Snippets**

Custom snippets are imported to the corresponding language in Ochtopus.

### What's Different (and Better)

While the basics are the same, you'll notice some improvements:

| Feature | VS Code | Ochtopus |
|---------|---------|----------|
| AI Autocomplete | Extension required | Built-in |
| AI Agent | Not available | Built-in |
| Performance | Good | Faster |
| Codebase Search | Basic | AI-powered |

### Tips for New Users

1. **Try AI features first**: Press Cmd/Ctrl + K to experience AI chat
2. **Explore the AI panel**: Cmd/Ctrl + Shift + A opens the AI Agent
3. **Keep your workflow**: Your shortcuts and settings work the same

Welcome to Ochtopus! We think you're going to love it here.
    `,
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200&h=600&fit=crop',
    author: {
      name: 'Alex Rivera',
      avatar: 'AR',
      role: 'Technical Writer',
      bio: 'Alex creates documentation and guides to help developers succeed with Ochtopus.'
    },
    date: 'Nov 28, 2025',
    readTime: '7 min read',
    category: 'Guides',
    tags: ['Migration', 'VS Code', 'Getting Started']
  },
  'ochtopus-ai-native-development': {
    slug: 'ochtopus-ai-native-development',
    title: 'The Future of Code: AI-Native Development',
    excerpt: 'What does the future of software development look like? We explore the trends shaping AI-native development and what it means for developers.',
    content: `
## A New Era of Software Development

We're witnessing the birth of a new paradigm: AI-native development. Just as mobile-first changed how we build applications, AI-native is changing how we write code.

### What is AI-Native Development?

AI-native development means building software with AI as a fundamental part of the development process, not an afterthought. It's the difference between:

- **AI-assisted**: Using AI tools occasionally
- **AI-native**: AI integrated into every step of development

### The Pillars of AI-Native Development

**1. Intent-Based Programming**

Instead of writing every line of code, developers express intent:

\`\`\`
// Traditional
function validateEmail(email) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
}

// AI-Native
// Intent: "Validate email addresses with proper error messages"
// AI generates comprehensive validation with edge cases
\`\`\`

**2. Continuous AI Review**

AI continuously analyzes code for:

- Security vulnerabilities
- Performance issues
- Code style inconsistencies
- Potential bugs

**3. Predictive Development**

AI predicts what you need before you ask:

- Suggesting tests based on code changes
- Identifying missing error handling
- Recommending optimizations

### The Developer's Evolving Role

In an AI-native world, developers become:

- **Architects**: Designing systems at a higher level
- **Reviewers**: Validating AI-generated code
- **Guides**: Directing AI toward the right solutions
- **Specialists**: Focusing on complex, creative problems

### Challenges Ahead

AI-native development isn't without challenges:

1. **Trust**: Learning to trust AI suggestions
2. **Understanding**: Maintaining code comprehension
3. **Quality**: Ensuring AI output meets standards
4. **Skills**: Adapting developer skills for new workflows

### Preparing for the Future

How can you prepare for AI-native development?

1. **Start using AI tools now**: Build familiarity
2. **Focus on architecture**: High-level design becomes more important
3. **Develop review skills**: Learn to evaluate AI code quickly
4. **Stay curious**: The field is evolving rapidly

The future is AI-native, and it's more exciting than ever to be a developer.
    `,
    image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200&h=600&fit=crop',
    author: {
      name: 'Lisa Park',
      avatar: 'LP',
      role: 'CTO',
      bio: 'Lisa is the CTO of Ochtopus, leading the technical vision for the future of AI-powered development.'
    },
    date: 'Nov 20, 2025',
    readTime: '10 min read',
    category: 'Insights',
    tags: ['Future', 'AI', 'Trends']
  }
};

// Get all posts as array for listing
export const getAllBlogPosts = () => Object.values(blogPostsData);

export function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? blogPostsData[slug] : null;

  if (!post) {
    return (
      <div className="pt-24 min-h-screen">
        <div className="container py-16 text-center">
          <h1 className="heading-lg mb-4">Post Not Found</h1>
          <p className="text-text-secondary mb-8">
            The blog post you're looking for doesn't exist.
          </p>
          <Link to="/blog" className="btn btn-primary">
            <ArrowLeft size={18} />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = `https://ochtopus.com/blog/${post.slug}`;

  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="section-sm">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-text-tertiary text-sm mb-6">
              <Link to="/" className="hover:text-accent">Home</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-accent">Blog</Link>
              <span>/</span>
              <span className="text-text-secondary">{post.category}</span>
            </div>

            {/* Category */}
            <span className="inline-block px-3 py-1 rounded-full bg-accent-muted text-accent text-sm font-medium mb-4">
              {post.category}
            </span>

            {/* Title */}
            <h1 className="heading-xl mb-6">{post.title}</h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-accent-muted flex items-center justify-center text-accent font-semibold">
                  {post.author.avatar}
                </div>
                <div>
                  <div className="font-medium">{post.author.name}</div>
                  <div className="text-text-tertiary text-sm">{post.author.role}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-text-secondary text-sm">
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {post.date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  {post.readTime}
                </span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="rounded-xl overflow-hidden mb-8">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-sm">
        <div className="container">
          <div className="grid lg:grid-cols-[1fr_280px] gap-12">
            {/* Article Content */}
            <article className="max-w-3xl">
              <div
                className="prose prose-lg prose-invert max-w-none"
                style={{
                  '--tw-prose-body': 'var(--color-text-secondary)',
                  '--tw-prose-headings': 'var(--color-text-primary)',
                  '--tw-prose-links': 'var(--color-accent)',
                  '--tw-prose-code': 'var(--color-accent)',
                } as React.CSSProperties}
              >
                {/* Render markdown content as HTML */}
                <div className="blog-content">
                  {post.content.split('\n').map((line, i) => {
                    if (line.startsWith('## ')) {
                      return <h2 key={i} className="heading-lg mt-12 mb-6">{line.replace('## ', '')}</h2>;
                    }
                    if (line.startsWith('### ')) {
                      return <h3 key={i} className="heading-md mt-8 mb-4">{line.replace('### ', '')}</h3>;
                    }
                    if (line.startsWith('**') && line.endsWith('**')) {
                      return <p key={i} className="font-semibold mt-6 mb-2">{line.replace(/\*\*/g, '')}</p>;
                    }
                    if (line.startsWith('```')) {
                      return null; // Skip code fence markers
                    }
                    if (line.startsWith('- ')) {
                      return <li key={i} className="text-text-secondary ml-4">{line.replace('- ', '')}</li>;
                    }
                    if (line.startsWith('1. ') || line.startsWith('2. ') || line.startsWith('3. ') || line.startsWith('4. ')) {
                      return <li key={i} className="text-text-secondary ml-4 list-decimal">{line.replace(/^\d+\.\s/, '')}</li>;
                    }
                    if (line.startsWith('> ')) {
                      return (
                        <blockquote key={i} className="border-l-4 border-accent pl-4 my-6 italic text-text-secondary">
                          {line.replace('> ', '')}
                        </blockquote>
                      );
                    }
                    if (line.startsWith('|')) {
                      return null; // Skip table rows for simplicity
                    }
                    if (line.trim() === '') {
                      return <br key={i} />;
                    }
                    if (line.startsWith('---')) {
                      return <hr key={i} className="my-8 border-border-default" />;
                    }
                    return <p key={i} className="text-text-secondary mb-4">{line}</p>;
                  })}
                </div>
              </div>

              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-border-default">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-text-tertiary text-sm">Tags:</span>
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-bg-card border border-border-default text-text-secondary text-sm hover:border-accent cursor-pointer transition-colors"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Author Bio */}
              <div className="mt-8 p-6 rounded-xl bg-bg-card border border-border-default">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-accent-muted flex items-center justify-center text-accent text-xl font-semibold flex-shrink-0">
                    {post.author.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-lg">{post.author.name}</div>
                    <div className="text-accent text-sm mb-2">{post.author.role}</div>
                    <p className="text-text-secondary">{post.author.bio}</p>
                  </div>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-28 space-y-6">
                {/* Share */}
                <div className="card">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Share2 size={18} />
                    Share this article
                  </h3>
                  <div className="flex gap-2">
                    <a
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-lg bg-bg-elevated hover:bg-accent-glow transition-colors"
                    >
                      <Twitter size={18} />
                    </a>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-lg bg-bg-elevated hover:bg-accent-glow transition-colors"
                    >
                      <Linkedin size={18} />
                    </a>
                    <button
                      onClick={() => navigator.clipboard.writeText(shareUrl)}
                      className="p-3 rounded-lg bg-bg-elevated hover:bg-accent-glow transition-colors"
                    >
                      <LinkIcon size={18} />
                    </button>
                  </div>
                </div>

                {/* Related Posts */}
                <div className="card">
                  <h3 className="font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    {getAllBlogPosts()
                      .filter(p => p.slug !== post.slug && p.category === post.category)
                      .slice(0, 3)
                      .map((relatedPost) => (
                        <Link
                          key={relatedPost.slug}
                          to={`/blog/${relatedPost.slug}`}
                          className="block group"
                        >
                          <h4 className="font-medium text-sm group-hover:text-accent transition-colors line-clamp-2">
                            {relatedPost.title}
                          </h4>
                          <span className="text-text-tertiary text-xs">{relatedPost.readTime}</span>
                        </Link>
                      ))}
                  </div>
                </div>

                {/* Newsletter */}
                <div className="card bg-accent-glow border-accent/20">
                  <h3 className="font-semibold mb-2">Stay Updated</h3>
                  <p className="text-text-secondary text-sm mb-4">
                    Get the latest articles delivered to your inbox.
                  </p>
                  <input
                    type="email"
                    placeholder="Your email"
                    className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none text-sm mb-2"
                  />
                  <button className="btn btn-primary btn-sm w-full">
                    Subscribe
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Back to Blog */}
      <section className="section-sm border-t border-border-default">
        <div className="container">
          <Link to="/blog" className="inline-flex items-center gap-2 text-text-secondary hover:text-accent transition-colors">
            <ArrowLeft size={18} />
            Back to all articles
          </Link>
        </div>
      </section>
    </div>
  );
}
